import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { Loader } from '@keethealth/keet-ui';
import queries from 'graph/queries';
import { Radio } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import Panel from 'common/Panel';
import TitlePanel from 'common/TitlePanel';
import moment from 'moment';
import { findNext } from 'utils/utils';

import AssignedList from './AssignedList';
// import DateChanger from './DateChanger';
// import FormBanner from './FormBanner';

import '../care_plan.css';

const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;

export class CarePlan extends Component {
  static defaultProps = {
    carePlans: [],
    loading: false,
  }

  static propTypes = {
    carePlanStats: PropTypes.shape({
      incomplete: PropTypes.array,
      complete: PropTypes.array,
      total: PropTypes.array,
    }).isRequired,
    carePlans: PropTypes.arrayOf(PropTypes.shape({
      carePlanActivities: PropTypes.arrayOf(PropTypes.shape({
        plannable: PropTypes.shape({
          title: PropTypes.string,
        }),
      })),
    })),
    match: PropTypes.shape({
      params: PropTypes.shape({
        taskId: PropTypes.string,
      }),
    }).isRequired,
    loading: PropTypes.bool.isRequired,
  }

  state = {
    tasksLoading: true,
    filteredTasks: 'all',
  }

  getNextActivity = () => {
    const {
      carePlans,
      match: { params: { taskId } },
    } = this.props;

    if (!_.isEmpty(carePlans)) {
      const { carePlanActivities = [] } = carePlans[0];
      const currentIndex = _.findIndex(carePlanActivities, { id: taskId });
      const nextActivity = findNext(carePlanActivities, currentIndex, cpa => cpa.status !== 'completed');
      if (nextActivity) return nextActivity.id;
    }
    // return to beginning
    return null;
  }

  setFilters = (type) => {
    this.setState({
      tasksLoading: false,
      filteredTasks: type,
    });
  }

  getFilteredActivities = () => {
    const { carePlans } = this.props;
    const { filteredTasks } = this.state;
    const carePlanActivities = _.get(carePlans, '[0].carePlanActivities');
    const types = _.groupBy(carePlanActivities, obj => _.lowerCase(obj.plannable ? obj.plannable.__typename : 'missing').replace(/ /g, ''));

    switch (filteredTasks) {
      case 'education':
        return types.education;
      case 'survey':
        return types.questionnaire;
      default:
        return carePlanActivities;
    }
  }

  getTotalCompleted = (completed) => {
    let totalCompleted = 0;
    if (completed) {
      totalCompleted = completed.length;
    }
    return totalCompleted;
  }

  getTotalCompletedThisWeek = (completed = []) => {
    if (_.isEmpty(completed)) {
      return 0;
    }

    return _.reduce(completed, (total, currentTask) => {
      if (!currentTask) {
        return total;
      }

      const { updatedAt } = currentTask;
      const format = 'YYYY-MM-DD HH:mm:ss UTC';

      const completedAt = moment.utc(updatedAt, format);
      if (completedAt.isBetween(moment.utc().startOf('week'), moment.utc().endOf('week'), 'day', '[]')) {
        return total + 1;
      }

      return total;
    }, 0);
  }

  handleSetFilter = (e) => {
    this.setFilters(e.target.value);
  }

  renderRowData = () => {
    const { carePlanStats = {} } = this.props;
    const totalCompleted = this.getTotalCompleted(carePlanStats.complete || []);

    const completedThisWeek = this.getTotalCompletedThisWeek(carePlanStats.complete || []);

    const columns = [
      {
        name: 'My Tasks',
        value: (carePlanStats.incomplete || []).length,
      },
      {
        name: 'Tasks Completed This Week',
        value: completedThisWeek,
      },
      {
        name: 'Total Tasks Completed',
        value: totalCompleted,
      },
    ];

    return (
      columns.map(
        (item, i) => (
          <div className="db border-box w-third" key={item.name} >
            <div className={classNames('justify-center flex items-center flex-column h-100 justify-between pa2', i === 1 && 'bl br bw1')}>
              <div className="mb3 tc">{item.name}</div>
              <div className="f3">{item.value}</div>
            </div>
          </div>
        ),
      )
    );
  }

  render() {
    const { loading } = this.props;
    const { tasksLoading, filteredTasks } = this.state;
    const filteredActivities = this.getFilteredActivities();

    if (loading && _.isEmpty(filteredActivities)) return <Loader />;
    if (!loading && tasksLoading) this.setFilters('all');

    return (
      <Fragment>
        <TitlePanel className="flex flex-column">
          <div className="items-center h-100 flex w-100">
            <div className="flex w-100 relative mh0 cf db border-box h-auto">
              {this.renderRowData()}
            </div>
          </div>
        </TitlePanel>
        <Panel id="scene-careplan" className="flex flex-column pa3-ns">
          <div className="f4 bb pa3 tc">
            <RadioGroup>
              <RadioButton
                value="all"
                onClick={this.handleSetFilter}
                checked={filteredTasks === 'all'}
              >
                All Tasks
              </RadioButton>
              <RadioButton
                value="education"
                onClick={this.handleSetFilter}
                checked={filteredTasks === 'education'}
              >
                Education
              </RadioButton>
              <RadioButton
                value="survey"
                onClick={this.handleSetFilter}
                checked={filteredTasks === 'survey'}
              >
                Survey
              </RadioButton>
            </RadioGroup>
          </div>
          {/* <DateChanger selectedDate={selectedDate} /> */}
          {/* <FormBanner selectedDate={selectedDate} /> */}
          {!tasksLoading &&
            <AssignedList filteredActivities={filteredActivities} loading={tasksLoading} />
          }
        </Panel>
      </Fragment>
    );
  }
}

export default compose(
  graphql(queries.carePlansIncomplete, {
    options: {
      fetchPolicy: 'cache-and-network',
      pollInterval: 60000,
    },
    props: ({ data: { loading, carePlans } }) => ({
      carePlans,
      loading,
    }),
  }),
  graphql(queries.taskStats, {
    skip: ({ loading, carePlans }) => loading || !(carePlans && carePlans[0] && carePlans[0].id),
    options: (props) => {
      const { carePlans } = props;
      return {
        variables: { id: (carePlans && carePlans[0] && carePlans[0].id) },
        fetchPolicy: 'cache-and-network',
      };
    },
    props: ({ data: { loading, carePlanStats } }) => ({
      carePlanStats,
      loadingTaskStats: loading,
    }),
  }),
)(CarePlan);
